import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';

/**
 * Interfaz para paradas de colectivos
 */
export interface IParada {
  _id?: string;
  nombre?: string;
  codigo?: string;
  geojson?: {
    type?: string;
    coordinates?: [number, number];
  };
  coordenadas?: [number, number]; // Mantener por compatibilidad
  idLinea?: string;
  idRecorrido?: string;
  orden?: number;
  // Otros campos según necesidad
  [key: string]: any;
}

/**
 * ParadasLayerManager
 *
 * Manager para renderizar paradas de colectivos en el mapa.
 *
 * Características:
 * - Icono simple (parada.png)
 * - Text label con el nombre de la parada
 * - NO soporta clustering (las paradas no se agrupan)
 * - Manager simple sin estados dinámicos
 *
 * Icono: parada.png
 */
@Injectable({
  providedIn: 'root',
})
export class ParadasLayerManager extends BaseEntityLayerManager<IParada> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'paradas';
  readonly supportsClustering = false; // Las paradas no se agrupan
  readonly clusterColor: string | undefined = undefined; // No se usa porque no soporta clustering

  // IDs de layers
  private iconLayerId = 'paradas-icon-layer';
  private textLayerId = 'paradas-text-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte una parada a un Feature de GeoJSON
   */
  override entityToFeature(parada: IParada): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(parada);

    // Obtener coordenadas - priorizar geojson.coordinates
    let coordinates: [number, number];

    if (
      parada.geojson?.coordinates &&
      parada.geojson.coordinates.length === 2
    ) {
      coordinates = parada.geojson.coordinates;
    } else if (parada.coordenadas && parada.coordenadas.length === 2) {
      coordinates = parada.coordenadas;
    } else {
      // Fallback: Buenos Aires
      console.warn(`⚠️ Parada ${parada._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: parada._id,
        nombre: parada.nombre || parada.codigo || 'Parada',
        codigo: parada.codigo,
        orden: parada.orden,
        // Datos completos
      },
    };
  }

  /**
   * Icono de parada
   */
  override getIconExpression(): string {
    return 'parada-icon';
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para paradas
   *
   * Dos layers:
   * 1. Icono de parada
   * 2. Text label con el nombre
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. ICON LAYER (icono de parada)
    this.addLayer({
      id: this.iconLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.212, // PNG 165x165 → 35px (35/165 = 0.212)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-anchor': 'bottom', // Anclar en la parte inferior del icono
      },
    });

    // 2. TEXT LAYER (nombre de la parada)
    this.addLayer({
      id: this.textLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'text-field': ['get', 'nombre'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'top', // Texto encima del icono
        'text-offset': [0, 0.5], // Offset en Y para separar del icono
        'text-optional': true, // No mostrar si no hay espacio
      },
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    });
  }

  // ========== VISIBILIDAD ==========

  /**
   * Override de setVisibility para manejar ambos layers (icon + text)
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;
    const visibility = visible ? 'visible' : 'none';

    // Cambiar visibilidad de ambos layers
    if (this.map.getLayer(this.iconLayerId)) {
      this.map.setLayoutProperty(this.iconLayerId, 'visibility', visibility);
    }

    if (this.map.getLayer(this.textLayerId)) {
      this.map.setLayoutProperty(this.textLayerId, 'visibility', visibility);
    }
  }
}
